import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';

const AssessmentPage = () => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const questions = [
    {
      id: 1,
      question: "Where are you in your walk with God?",
      options: [
        { value: "Just beginning", label: "🌱 New to faith", icon: "🌱" },
        { value: "Growing", label: "🌿 Building my foundation", icon: "🌿" },
        { value: "Maturing", label: "🌳 Ready to step into leadership", icon: "🌳" },
        { value: "Seasoned", label: "🔥 Already in ministry", icon: "🔥" }
      ]
    },
    {
      id: 2,
      question: "What are you most hungry for right now?",
      options: [
        { value: "Understanding the Word", label: "📖 Deeper understanding of Scripture", icon: "📖" },
        { value: "Hearing God's voice", label: "🔮 Learning to hear God clearly", icon: "🔮" },
        { value: "Leading others", label: "👥 Making an impact through leadership", icon: "👥" },
        { value: "Worshiping with power", label: "🎵 Carrying God's presence in worship", icon: "🎵" }
      ]
    },
    {
      id: 3,
      question: "What do you sense God calling you to?",
      options: [
        { value: "Teaching/preaching", label: "🎤 Preaching and teaching the Word", icon: "🎤" },
        { value: "Worship/music", label: "🎶 Leading worship and music", icon: "🎶" },
        { value: "Mentoring/coaching", label: "🤝 Discipling and mentoring others", icon: "🤝" },
        { value: "Still discovering", label: "❓ Still figuring it out", icon: "❓" }
      ]
    },
    {
      id: 4,
      question: "What holds you back most?",
      options: [
        { value: "Lack of confidence", label: "😟 Fear and self-doubt", icon: "😟" },
        { value: "Need biblical knowledge", label: "📚 Lack of biblical training", icon: "📚" },
        { value: "Balancing calling", label: "⚖️ Too busy with life", icon: "⚖️" },
        { value: "Finding training", label: "🎯 Don't know where to get trained", icon: "🎯" }
      ]
    },
    {
      id: 5,
      question: "How do you prefer to learn?",
      options: [
        { value: "Video teachings", label: "🎥 Watching video lessons", icon: "🎥" },
        { value: "Reading books", label: "📖 Reading and studying books", icon: "📖" },
        { value: "Community discussions", label: "💬 Talking with others", icon: "💬" },
        { value: "One-on-one guidance", label: "👤 Personal mentoring", icon: "👤" }
      ]
    },
    {
      id: 6,
      question: "How would you describe your prayer life?",
      options: [
        { value: "Intimate/quiet", label: "🙏 Quiet and personal", icon: "🙏" },
        { value: "Passionate/prophetic", label: "🔥 Passionate and bold", icon: "🔥" },
        { value: "Musical/creative", label: "🎵 Through music and creativity", icon: "🎵" },
        { value: "Structured/liturgical", label: "📿 Structured and traditional", icon: "📿" }
      ]
    },
    {
      id: 7,
      question: "What do you want to grow in most?",
      options: [
        { value: "Prophetic accuracy", label: "🎯 Hearing and speaking prophetically", icon: "🎯" },
        { value: "Apostolic authority", label: "📢 Leading with spiritual authority", icon: "📢" },
        { value: "Prophetic worship", label: "🎼 Ministering through worship", icon: "🎼" },
        { value: "Spiritual warfare", label: "💪 Intercession and warfare", icon: "💪" }
      ]
    },
    {
      id: 8,
      question: "Are you ready to commit to 12 months of training?",
      options: [
        { value: "Fully committed", label: "✅ Yes, I'm all in", icon: "✅" },
        { value: "Very interested", label: "🤔 Tell me more first", icon: "🤔" },
        { value: "Want January start", label: "⏰ I want to start in January", icon: "⏰" },
        { value: "Just exploring", label: "🔍 Just looking around", icon: "🔍" }
      ]
    }
  ];

  const handleAnswerSelect = (questionId, answer) => {
    setAnswers({ ...answers, [questionId]: answer });
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Format answers for backend
      const formattedAnswers = Object.keys(answers).map(questionId => ({
        question_id: parseInt(questionId),
        answer: answers[questionId]
      }));
      
      const response = await fetch(`${backendUrl}/api/assessment/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          answers: formattedAnswers
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Navigate to results page with session_id and recommended track
        navigate(`/assessment/results?session=${data.session_id}&track=${data.recommended_track}`);
      } else {
        alert('Error submitting assessment. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting assessment:', error);
      alert('Failed to submit assessment. Please check your connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentQuestionData = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const isQuestionAnswered = answers[currentQuestionData.id] !== undefined;
  const allQuestionsAnswered = questions.every(q => answers[q.id] !== undefined);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-orange-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Sparkles className="w-6 h-6 text-purple-600" />
              <h1 className="text-xl font-semibold text-gray-900">Find Your Track</h1>
            </div>
            <button
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-gray-900 text-sm"
            >
              Exit
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-purple-600 to-orange-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-2 text-sm text-gray-600 text-center">
            Question {currentQuestion + 1} of {questions.length}
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">
            {currentQuestionData.question}
          </h2>

          <div className="space-y-4">
            {currentQuestionData.options.map((option) => {
              const isSelected = answers[currentQuestionData.id] === option.value;
              
              return (
                <button
                  key={option.value}
                  onClick={() => handleAnswerSelect(currentQuestionData.id, option.value)}
                  className={`w-full text-left p-6 rounded-xl border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-purple-600 bg-purple-50 shadow-md'
                      : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <span className="text-3xl">{option.icon}</span>
                    <span className={`text-lg ${isSelected ? 'font-semibold text-purple-900' : 'text-gray-700'}`}>
                      {option.label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between items-center">
            <button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all ${
                currentQuestion === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Previous</span>
            </button>

            {currentQuestion < questions.length - 1 ? (
              <button
                onClick={handleNext}
                disabled={!isQuestionAnswered}
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all ${
                  isQuestionAnswered
                    ? 'bg-gradient-to-r from-purple-600 to-orange-600 text-white hover:from-purple-700 hover:to-orange-700 shadow-lg'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                <span>Next</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!allQuestionsAnswered || isSubmitting}
                className={`flex items-center space-x-2 px-8 py-3 rounded-xl font-semibold transition-all ${
                  allQuestionsAnswered && !isSubmitting
                    ? 'bg-gradient-to-r from-purple-600 to-orange-600 text-white hover:from-purple-700 hover:to-orange-700 shadow-lg transform hover:scale-105'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                <span>{isSubmitting ? 'Processing...' : 'See My Results'}</span>
                <Sparkles className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentPage;
